
import React, { useState, lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import { Tabs, Tab, Spinner, Modal, Button } from 'react-bootstrap';
import { QuestionCircleFill } from 'react-bootstrap-icons';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SidebarProvider } from '../context/SidebarContext';
import SidebarContainer from '../components/common/SidebarContainer';
import ContentContainer from '../components/common/ContentContainer';
import Breadcrumb from '../components/common/Breadcrumb';
import SoporteView from '../components/common/SoporteView';
import ProductTour from '../components/common/ProductTour';
import { useCurriculum } from '../context/CurriculumContext';
import { useAuth } from '../context/AuthContext';
import { deleteUnit0 as deleteUnit0API, linkAIGenerationToPlannedClass } from '../api';
import { ENDPOINTS } from '../endpoints';
import useOAClassMatrix from '../hooks/useOAClassMatrix';
import { usePMStyles } from '../components/teacher/TeacherModalStyles';
import { useFeedback } from '../context/FeedbackContext';
import '../styles/global-sidebar.css';

// Pasos del tour de producto de "Mis Clases" (Vista Docente). Los pasos cuyo
// elemento no exista en el DOM al momento de iniciar el tour se omiten
// automáticamente (ver ProductTour) — así el mismo recorrido funciona tanto
// si el docente ya seleccionó asignatura/curso/sección como si todavía no.
const MIS_CLASES_TOUR_STEPS = [
  {
    selector: '[data-tour="curriculum-selector"]',
    title: 'Elige qué vas a planificar',
    content: 'Selecciona la asignatura, el curso y la sección de curso para ver su contenido curricular y su calendario de planificación.',
  },
  {
    selector: '[data-tour="curriculum-accordion"]',
    title: 'Unidades y Objetivos de Aprendizaje',
    content: 'Aquí ves las unidades del curso y sus OA. Desde acá también puedes replicar una planificación o limpiarla.',
  },
  {
    selector: '[data-tour="bulk-ai-button"]',
    title: 'Genera planificaciones con IA en lote',
    content: 'Genera con Inteligencia Artificial las clases de un rango de fechas de una sola vez, en vez de clase por clase.',
  },
  {
    selector: '[data-tour="planning-calendar"]',
    title: 'Calendario de Planificación',
    content: 'Cada celda es una clase. El número indica cuántos OA tiene asignados; el color indica el tipo de clase o si ya está planificada. Haz clic en una celda para abrir su planificación.',
  },
  {
    selector: '.calendar-scroll-arrow-left',
    title: 'Navega el calendario',
    content: 'Usa estas flechas para desplazarte por las fechas. Mantén presionado para moverte de forma continua.',
  },
  {
    selector: '#teacher-view-tabs-tab-progress',
    title: 'Avance Curricular',
    content: 'En esta pestaña puedes ver el seguimiento de ejecución de las clases ya planificadas.',
  },
];

const MIS_CLASES_TOUR_STORAGE_KEY = 'artemis_mis_clases_tour_seen';

// Lazy loading de componentes
const CurriculumSelector = lazy(() => import('../components/teacher/CurriculumSelector'));
const CurriculumAccordion = lazy(() => import('../components/teacher/CurriculumAccordion'));
const PlanningCalendar = lazy(() => import('../components/teacher/PlanningCalendar'));
const PlanningModal = lazy(() => import('../components/teacher/PlanningModalRouter'));
const UnitOAManager = lazy(() => import('../components/teacher/UnitOAManager'));
const Unit0OAManager = lazy(() => import('../components/teacher/Unit0OAManager'));
const TeacherDashboard = lazy(() => import('../components/teacher/TeacherDashboard'));
const ClassExecutionCalendar = lazy(() => import('../components/teacher/ClassExecutionCalendar'));
const ErrorModal = lazy(() => import('../components/common/ErrorModal'));
const BulkAIPromptModal = lazy(() => import('../components/teacher/BulkAIPromptModal'));
const TeacherPlanningMonitor = lazy(() => import('../components/teacher/TeacherPlanningMonitor'));

// Componente de carga
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
    <Spinner animation="border" role="status">
      <span className="visually-hidden">Cargando...</span>
    </Spinner>
  </div>
);

const TeacherView = () => {
  useEffect(() => { document.title = 'Artemis — Docente'; }, []);
  usePMStyles();
  const feedback = useFeedback();

  const {
    courses,
    units,
    oas,
    loading,
    loadingContent,
    selectedCourse,
    selectedSubject,
    selectedCourseSection,
    selectionComplete,
    hasUnit0,
    unit0OAs,
    shouldDeleteUnit0,
    handleCourseSelection,
    handleSelectionComplete,
    resetSelection,
    createUnit0,
    deleteUnit0,
    addOAToUnit0,
    updateOAInUnit0,
    deleteOAFromUnit0,
    applyUnitValidDates,
  } = useCurriculum();

  const { user } = useAuth();
  const location = useLocation();

  // Tour de producto de "Mis Clases": se ofrece automáticamente una vez por
  // docente la primera vez que ve el módulo con contenido ya cargado (para
  // que la mayoría de los pasos tengan dónde apuntar), y puede volver a
  // verse en cualquier momento con el botón "Ver tour".
  const [runTour, setRunTour] = useState(false);
  const tourStorageKey = `${MIS_CLASES_TOUR_STORAGE_KEY}_${user?.organization_person_role_id || 'anon'}`;

  const handleCloseTour = () => {
    setRunTour(false);
    localStorage.setItem(tourStorageKey, '1');
  };

  // State for Planning Modal
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  // Aviso al intentar abrir la planificación de una clase sin OA asociados.
  const [showNoOAWarning, setShowNoOAWarning] = useState(false);
  const [planningModalData, setPlanningModalData] = useState(null);

  // State for Bulk AI Planning Modal
  const [showBulkAIModal, setShowBulkAIModal] = useState(false);

  // State for pending planned classes (in memory, not yet persisted)
  const [pendingPlannedClasses, setPendingPlannedClasses] = useState({});

  // Keys (class_date) agregadas por la última "Replicar planificación" sin
  // guardar todavía — se limpian si se confirma otra replicación encima
  // (evita duplicados al reintentar con otra fecha base, ver docs/oa_modif.md §2.5)
  const lastReplicatedKeysRef = useRef([]);
  
  // Key para forzar recarga del calendario después de guardar
  const [calendarKey, setCalendarKey] = useState(0);

  // Matriz OA x Clase compartida por todas las mini-matrices por unidad del
  // acordeón (CurriculumAccordion) y por el botón único "Actualizar" del
  // calendario. Se eleva aquí para que ambos lean el mismo estado de cambios
  // pendientes (pendingCount) y disparen el mismo guardado (saveChanges).
  const oaClassMatrix = useOAClassMatrix(selectedCourseSection);

  // Token que fuerza a PlanningCalendar a recargar SOLO las clases planificadas
  // (sin remontar ni re-consultar horario/semestre/eventos como hace calendarKey).
  const [calendarReloadToken, setCalendarReloadToken] = useState(0);

  // Confirmación al desasociar un OA de una clase que YA está planificada: si el
  // docente continúa, se descarta la planificación de esa clase. Guarda el modo
  // ('save' = botón Actualizar; 'unassign' = "Desasociar" fuera de rango), las
  // clases afectadas (para mostrar su objetivo) y el payload pendiente.
  const [clearPlanningConfirm, setClearPlanningConfirm] = useState(null);

  const doSaveMatrix = useCallback(async (clearPlannedSlots) => {
    const result = await oaClassMatrix.saveChanges(undefined, { clearPlannedSlots });
    if (result && result.ok) {
      setCalendarReloadToken(prev => prev + 1);
    }
  }, [oaClassMatrix]);

  const doUnassignMatrix = useCallback(async (keys, clearPlannedSlots) => {
    const result = await oaClassMatrix.unassignKeys(keys, { clearPlannedSlots });
    if (result && result.ok) {
      setCalendarReloadToken(prev => prev + 1);
    }
  }, [oaClassMatrix]);

  const handleUpdateMatrix = useCallback(async () => {
    // Cualquier cambio (marcar u desmarcar OA) sobre una clase que YA tiene
    // planificación dispara el aviso.
    const pendingKeys = Object.keys(oaClassMatrix.pendingChanges);
    const targets = oaClassMatrix.getPlannedClassChangeTargets(pendingKeys, { includeAdds: true });
    if (targets.length > 0) {
      setClearPlanningConfirm({ mode: 'save', targets });
      return;
    }
    await doSaveMatrix();
  }, [oaClassMatrix, doSaveMatrix]);

  // "Desasociar" clases con OA fuera del rango de una unidad: persiste de
  // inmediato (checked:false) y refresca el calendario.
  const handleUnassignMatrixKeys = useCallback(async (keys) => {
    const targets = oaClassMatrix.getPlannedClassChangeTargets(keys);
    if (targets.length > 0) {
      setClearPlanningConfirm({ mode: 'unassign', targets, keys });
      return;
    }
    await doUnassignMatrix(keys);
  }, [oaClassMatrix, doUnassignMatrix]);

  const handleConfirmClearPlanning = useCallback(async () => {
    const confirm = clearPlanningConfirm;
    setClearPlanningConfirm(null);
    if (!confirm) return;
    const clearPlannedSlots = new Set(confirm.targets.map(t => t.slot));
    if (confirm.mode === 'unassign') {
      await doUnassignMatrix(confirm.keys, clearPlannedSlots);
    } else {
      await doSaveMatrix(clearPlannedSlots);
    }
  }, [clearPlanningConfirm, doSaveMatrix, doUnassignMatrix]);

  // "Restaurar": deja intactas las clases planificadas (vuelve a marcar sus
  // checkbox) y no toca su planificación. El resto de cambios sigue su curso —
  // en modo 'save' quedan pendientes de Actualizar; en modo 'unassign' se
  // desasocian igual las clases que no tienen planificación.
  const handleRestoreClearPlanning = useCallback(async () => {
    const confirm = clearPlanningConfirm;
    setClearPlanningConfirm(null);
    if (!confirm) return;
    const restoredKeys = confirm.targets.flatMap(t => t.keys || []);
    if (confirm.mode === 'unassign') {
      const restored = new Set(restoredKeys);
      const remaining = (confirm.keys || []).filter(k => !restored.has(k));
      if (remaining.length > 0) await doUnassignMatrix(remaining);
    } else {
      oaClassMatrix.revertPendingKeys(restoredKeys);
    }
  }, [clearPlanningConfirm, doUnassignMatrix, oaClassMatrix]);
  
  // Mapeo persistente de IDs temporales a reales para OAs de Unidad 0
  const [unit0OAMapping, setUnit0OAMapping] = useState({});
  
  // Estado para controlar la carga del calendario
  const [loadingCalendar, setLoadingCalendar] = useState(false);

  // Tour de producto de "Mis Clases": se ofrece automáticamente una vez por
  // docente la primera vez que ve el módulo con contenido ya cargado (para
  // que la mayoría de los pasos tengan dónde apuntar), y puede volver a
  // verse en cualquier momento con el botón "Ver tour".
  useEffect(() => {
    if (location.pathname !== '/docente/clases') return;
    if (!selectionComplete || loadingContent || loadingCalendar) return;
    if (localStorage.getItem(tourStorageKey)) return;
    const t = setTimeout(() => setRunTour(true), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, selectionComplete, loadingContent, loadingCalendar]);

  // State for Unit OA Manager Modal
  const [showUnitOAModal, setShowUnitOAModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);

  // State for Unit 0 OA Manager Modal
  const [showUnit0OAModal, setShowUnit0OAModal] = useState(false);

  // State for Error Modal
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);

  const handleSelectionCompleteLocal = useCallback((selection) => {
    setLoadingCalendar(true); // Reiniciar estado de carga del calendario
    handleSelectionComplete(selection);
  }, [handleSelectionComplete]);

  const handleCalendarLoadingChange = useCallback((isLoading) => {
    console.log('[TeacherView] Calendar loading state changed:', isLoading);
    setLoadingCalendar(isLoading);
  }, []);

  const handleBackToSelection = () => {
    setPendingPlannedClasses({});
    lastReplicatedKeysRef.current = [];
    resetSelection();
  };

  const handleCellClick = (data) => {
    // Una clase no se puede planificar sin OA asociados (se asignan desde la
    // matriz OA x Clase). Si no tiene, se avisa en vez de abrir la planificación.
    const hasOAs = (data?.classData?.competency_definition_ids || []).length > 0;
    if (!hasOAs) {
      setShowNoOAWarning(true);
      return;
    }
    setPlanningModalData({ ...data, block: data.block || 1 });
    setShowPlanningModal(true);
  };

  // Navegación "Anterior"/"Siguiente" dentro del modal de Planificación entre
  // clases con OA asociado (delegado a PlanningCalendar, que es quien conoce
  // el orden real fecha/bloque de las clases de la sección).
  const planningCalendarRef = useRef(null);

  const handleNavigatePlanning = (direction) => {
    if (!planningModalData || !planningCalendarRef.current) return;
    const target = planningCalendarRef.current.getAdjacentPlannedSlotWithOA(
      planningModalData.date,
      planningModalData.block || 1,
      direction
    );
    if (!target) return;
    setPlanningModalData({ date: target.date, block: target.block || 1, classData: target.classData });
  };

  const prevPlanningSlot = (planningModalData && planningCalendarRef.current)
    ? planningCalendarRef.current.getAdjacentPlannedSlotWithOA(planningModalData.date, planningModalData.block || 1, -1)
    : null;
  const nextPlanningSlot = (planningModalData && planningCalendarRef.current)
    ? planningCalendarRef.current.getAdjacentPlannedSlotWithOA(planningModalData.date, planningModalData.block || 1, 1)
    : null;

  const handleClosePlanningModal = () => {
    setShowPlanningModal(false);
    setPlanningModalData(null);
  };

  const handleSavePlan = (planData, newFiles = [], existingAttachmentPaths = []) => {
    // Guardar en memoria local (no persistir aún al backend).
    // La clase se identifica por (fecha, bloque) — una sección puede tener
    // varias clases el mismo día en distintos bloques del horario.
    const key = `${planData.class_date}#${planData.block_number || 1}`;

    console.log('[TeacherView] Guardando planificación en memoria:', key, planData);
    console.log('[TeacherView] Archivos nuevos:', newFiles.length);
    console.log('[TeacherView] Paths de archivos existentes a mantener:', existingAttachmentPaths);
    
    // Incluir archivos en el objeto de datos pendientes
    const planDataWithFiles = {
      ...planData,
      _newFiles: newFiles,  // Archivos nuevos (File objects)
      _existingAttachmentPaths: existingAttachmentPaths  // Paths de archivos existentes a mantener
    };
    
    setPendingPlannedClasses(prev => ({
      ...prev,
      [key]: planDataWithFiles
    }));

    // Cerrar el modal sin recargar
    handleClosePlanningModal();

    // Mostrar mensaje de confirmación
    feedback.info('Planificación guardada en memoria. Haz clic en "Guardar" para persistir los cambios.');
  };


  const handleManageOAsClick = (unit) => {
    if (unit.short_name === 'UN0') {
      // Para Unidad 0, abrir el gestor específico
      setShowUnit0OAModal(true);
    } else {
      // Para unidades regulares, usar el gestor normal
      setSelectedUnit(unit);
      setShowUnitOAModal(true);
    }
  };

  const handleCloseUnitOAModal = () => {
    setShowUnitOAModal(false);
    setSelectedUnit(null);
  };

  const handleCloseUnit0OAModal = () => {
    setShowUnit0OAModal(false);
  };

  // Limpiar mapeo cuando se cambie de curso o se elimine Unidad 0
  useEffect(() => {
    setUnit0OAMapping({});
  }, [selectedCourse, selectedCourseSection]);

  const handleSavePlanning = async () => {
    try {
      console.log('[TeacherView] Guardando planificación completa...');
      console.log('[TeacherView] Planificaciones pendientes:', pendingPlannedClasses);
      
      // Variable para almacenar estadísticas de actualización
      let oaUpdateStats = { regular: 0, unit0: 0, failed: 0 };
      let unit0Id = null;
      let unit0OAMapping = {}; // Mapeo de IDs temporales a IDs reales
      
      // PASO 0: Eliminar Unidad 0 del backend si es necesario
      if (shouldDeleteUnit0) {
        console.log('[TeacherView] PASO 0: Eliminando Unidad 0 del backend...');
        try {
          await deleteUnit0API(selectedCourse);
          console.log('[TeacherView] Unidad 0 eliminada del backend exitosamente');
        } catch (error) {
          console.error('[TeacherView] Error al eliminar Unidad 0 del backend:', error);
          // Continuar con el proceso aunque falle la eliminación
        }
      }

      const token = localStorage.getItem('artemis_token');

      // PASO 1: Crear Unidad 0 si existe
      if (unit0OAs.length > 0) {
        console.log('[TeacherView] PASO 1: Creando Unidad 0...');
        const createUnit0Response = await fetch(ENDPOINTS.ADMIN_CREATE_UNIT0, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            course_id: parseInt(selectedCourse),
            course_section_id: parseInt(selectedCourseSection)
          })
        });

        if (!createUnit0Response.ok) {
          const errorData = await createUnit0Response.json();
          console.error('[TeacherView] Error al crear Unidad 0:', errorData);
          throw new Error(`Error al crear Unidad 0: ${errorData.detail || 'Error desconocido'}`);
        }

        const createUnit0Result = await createUnit0Response.json();
        unit0Id = createUnit0Result.competency_definition_id;
        console.log('[TeacherView] Unidad 0 creada con ID:', unit0Id);
      }

      // PASO 2: Duplicar OAs para Unidad 0 si existe
      if (unit0OAs.length > 0 && unit0Id) {
        console.log('[TeacherView] PASO 2: Duplicando OAs para Unidad 0...');
        const duplicateOAsResponse = await fetch(ENDPOINTS.ADMIN_DUPLICATE_OAS_FOR_UNIT0, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            unit0_id: unit0Id,
            course_id: selectedCourse,
            oas: unit0OAs.map(oa => ({
              original_oa_id: oa.original_competency_definition_id,
              valid_start_date: oa.valid_start_date,
              valid_end_date: oa.valid_end_date
            }))
          })
        });

        if (!duplicateOAsResponse.ok) {
          const errorData = await duplicateOAsResponse.json();
          console.error('[TeacherView] Error al duplicar OAs:', errorData);
          throw new Error(`Error al duplicar OAs para Unidad 0: ${errorData.detail || 'Error desconocido'}`);
        }

        const duplicateOAsResult = await duplicateOAsResponse.json();
        oaUpdateStats.unit0 = duplicateOAsResult.success_count || 0;
        console.log('[TeacherView] OAs duplicados para Unidad 0:', duplicateOAsResult);
        
        // Crear mapeo de IDs temporales a IDs reales
        if (duplicateOAsResult.created_oas) {
          const newMapping = { ...unit0OAMapping };
          duplicateOAsResult.created_oas.forEach((oa, index) => {
            const tempId = unit0OAs[index].competency_definition_id; // ID temporal del frontend
            const realId = oa.new_oa_id; // ID real de la base de datos
            newMapping[tempId] = realId;
            console.log('[TeacherView] Mapeo de ID:', tempId, '->', realId);
          });
          setUnit0OAMapping(newMapping);
        }
      }

      // Persistir clases planificadas pendientes (usando FormData para soportar archivos).
      // La asociación con OA ya no se guarda aquí — se gestiona por separado en el
      // módulo Planificación (matriz OA x Clase, ver docs/oa_modif.md).
      const savedClasses = [];
      for (const [key, planData] of Object.entries(pendingPlannedClasses)) {
        console.log('[TeacherView] Guardando clase planificada:', key);
        try {
          // Extraer archivos y metadatos internos del objeto de datos
          const { _newFiles, _existingAttachmentPaths, _generationLogId, ...cleanPlanData } = planData;
          const finalPlanData = cleanPlanData;

          // Bloque horario dentro del día (de la clave "fecha#bloque" o del payload).
          const blockNumber = parseInt(key.split('#')[1], 10) || finalPlanData.block_number || 1;

          // Crear FormData para enviar datos y archivos
          const formData = new FormData();
          formData.append('course_section_id', finalPlanData.course_section_id);
          formData.append('organization_person_role_id', finalPlanData.organization_person_role_id);
          formData.append('class_date', finalPlanData.class_date);
          formData.append('block_number', blockNumber);
          formData.append('type', finalPlanData.type);
          
          if (finalPlanData.objective) {
            formData.append('objective', finalPlanData.objective);
          }
          
          if (finalPlanData.stages) {
            formData.append('stages', JSON.stringify(finalPlanData.stages));
          }

          if (finalPlanData.plan_template) {
            formData.append('plan_template', finalPlanData.plan_template);
          }

          if (finalPlanData.template_fields) {
            formData.append('template_fields', JSON.stringify(finalPlanData.template_fields));
          }

          if (finalPlanData.comments && finalPlanData.comments.length > 0) {
            formData.append('comments', JSON.stringify(finalPlanData.comments));
          }
          
          // Agregar planned_class_id si existe (para actualizaciones)
          if (finalPlanData.planned_class_id) {
            console.log('[TeacherView] Enviando planned_class_id para actualización:', finalPlanData.planned_class_id);
            formData.append('planned_class_id', finalPlanData.planned_class_id);
          }

          // OA asociados a la clase: el backend valida que una clase no se guarde
          // sin OA. La asociación en sí la gestiona la matriz OA x Clase.
          if (finalPlanData.competency_definition_ids && finalPlanData.competency_definition_ids.length > 0) {
            formData.append('competency_definition_ids', JSON.stringify(finalPlanData.competency_definition_ids));
          }
          
          // Agregar archivos nuevos
          if (_newFiles && _newFiles.length > 0) {
            console.log('[TeacherView] Agregando', _newFiles.length, 'archivos nuevos al FormData');
            _newFiles.forEach(file => {
              formData.append('files', file);
            });
          }
          
          // Agregar paths de archivos existentes a mantener (importante para actualizaciones)
          if (_existingAttachmentPaths && _existingAttachmentPaths.length > 0) {
            console.log('[TeacherView] Enviando paths de archivos existentes a mantener:', _existingAttachmentPaths);
            formData.append('keep_attachments', JSON.stringify(_existingAttachmentPaths));
          } else if (planData.planned_class_id) {
            // Si es una actualización pero no hay paths, enviar array vacío para eliminar todos los archivos existentes
            console.log('[TeacherView] No hay archivos existentes a mantener, enviando array vacío');
            formData.append('keep_attachments', JSON.stringify([]));
          }
          
          // Agregar metadatos de archivos replicados (referencias a archivos de otra clase)
          // Estos archivos NO se duplican físicamente, solo se guardan sus metadatos
          if (finalPlanData.replicated_attachments && finalPlanData.replicated_attachments.length > 0) {
            console.log('[TeacherView] Enviando', finalPlanData.replicated_attachments.length, 'archivos replicados (referencias)');
            formData.append('replicated_attachments', JSON.stringify(finalPlanData.replicated_attachments));
          }
          
          const token = localStorage.getItem('artemis_token');
          const response = await fetch(ENDPOINTS.PLANNED_CLASSES, {
            method: 'POST',
            // NO establecer Content-Type - el navegador lo hace automáticamente
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData
          });

          if (response.ok) {
            const result = await response.json();
            savedClasses.push(result);
            console.log('[TeacherView] Clase guardada:', result);

            // Vincular la generación de IA (si esta clase provino de una) con la
            // clase planificada aceptada — aigenerationlog.plannedclassid / wasaccepted.
            const savedPlannedClassId = result.planned_class_id || finalPlanData.planned_class_id;
            if (_generationLogId && savedPlannedClassId) {
              await linkAIGenerationToPlannedClass(_generationLogId, savedPlannedClassId, true);
            }

            // Si la clase es nueva (replicación) y trae OA, persistir sus
            // asociaciones vía la matriz OA x Clase (docs/oa_modif.md). Para
            // clases ya existentes (planned_class_id presente) los OA ya están
            // asociados y no se re-escriben desde aquí.
            if (
              !finalPlanData.planned_class_id &&
              finalPlanData.competency_definition_ids &&
              finalPlanData.competency_definition_ids.length > 0
            ) {
              const matrixChanges = finalPlanData.competency_definition_ids.map(oaId => ({
                class_date: finalPlanData.class_date,
                block_number: blockNumber,
                competency_definition_id: oaId,
                checked: true,
              }));
              await fetch(ENDPOINTS.OA_CLASS_MATRIX_SAVE(finalPlanData.course_section_id), {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ changes: matrixChanges }),
              });
            }
          } else {
            const errorData = await response.json();
            console.error('[TeacherView] Error al guardar clase:', errorData);
            throw new Error(`Error al guardar clase ${key}: ${errorData.detail || 'Error desconocido'}`);
          }
        } catch (error) {
          console.error('[TeacherView] Error al guardar clase:', error);
          throw error;
        }
      }

      // Limpiar planificaciones pendientes después de guardar exitosamente
      setPendingPlannedClasses({});
      lastReplicatedKeysRef.current = [];

      // Recargar datos del currículum para obtener OAs duplicados con información completa
      console.log('[TeacherView] Recargando datos del currículum después de guardar...');
      await handleCourseSelection(selectedCourse, selectedCourseSection);
      
      // Forzar recarga del calendario para que muestre las clases guardadas desde el backend
      // Incrementar el key fuerza a React a remontar el componente y recargar datos
      setCalendarKey(prev => prev + 1);

      // Este flujo también persiste asociaciones OA (clases replicadas) vía la
      // matriz OA x Clase; recargarla para que las mini-matrices no queden stale.
      oaClassMatrix.loadMatrix();

      // Pequeño delay para asegurar que el backend haya procesado todos los cambios
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log('[TeacherView] Recarga completada. Los cambios deberían estar visibles ahora.');
      
      // Construir mensaje de éxito con estadísticas
      let successMessage = 'Planificación guardada exitosamente.\n';

      if (oaUpdateStats.regular > 0 || oaUpdateStats.unit0 > 0) {
        successMessage += `\nOAs actualizados:`;
        if (oaUpdateStats.regular > 0) {
          successMessage += `\n- ${oaUpdateStats.regular} OA${oaUpdateStats.regular !== 1 ? 's' : ''} regular${oaUpdateStats.regular !== 1 ? 'es' : ''}`;
        }
        if (oaUpdateStats.unit0 > 0) {
          successMessage += `\n- ${oaUpdateStats.unit0} OA${oaUpdateStats.unit0 !== 1 ? 's' : ''} de Unidad 0 creados/actualizados en BD`;
        }
      }

      if (savedClasses.length > 0) {
        successMessage += `\n\nClases planificadas guardadas: ${savedClasses.length}`;
      }

      feedback.success(successMessage);
      console.log('[TeacherView] Planificación completa guardada - manteniéndose en la vista actual');
    } catch (error) {
      console.error('[TeacherView] Error saving planning:', error);
      setErrorDetails(error.message);
      setShowErrorModal(true);
    }
  };

  const handleReplicatePlanning = useCallback((replicatedClasses) => {
    // La clase se identifica por (fecha, bloque); sus OA asociados viajan en
    // competency_definition_ids y se persisten aparte, vía la matriz OA x Clase,
    // al guardar (ver handleSavePlanning). La replicación opera por bloque.
    const newPending = {};
    replicatedClasses.forEach(cls => {
      const block = cls.block_number || 1;
      const key = `${cls.class_date}#${block}`;
      newPending[key] = {
        competency_definition_ids: cls.competency_definition_ids || [],
        course_section_id: parseInt(selectedCourseSection),
        class_date: cls.class_date,
        block_number: block,
        type: cls.type,
        objective: cls.objective || '',
        stages: cls.stages || null,
        approval_status: 'Por aprobar',
        replicated_attachments: (Array.isArray(cls.attachments) ? cls.attachments : [])
          .filter(att => att && att.path)
          .map(att => ({
            filename: att.filename || att.name || '',
            stored_filename: att.stored_filename || att.filename || '',
            path: att.path || '',
            url: att.url || '',
            size: att.size || 0,
            mime_type: att.mime_type || 'application/octet-stream',
            uploaded_at: att.uploaded_at || '',
            is_reference: true,
          })),
        _newFiles: [],
        _existingAttachmentPaths: [],
      };
    });
    setPendingPlannedClasses(prev => {
      const next = { ...prev };
      // Descarta la tanda de una replicación anterior no guardada, para no
      // duplicar clases si el docente reintenta con otra fecha base.
      lastReplicatedKeysRef.current.forEach(key => delete next[key]);
      return { ...next, ...newPending };
    });
    lastReplicatedKeysRef.current = Object.keys(newPending);
  }, [selectedCourseSection]);

  const handlePlanningCleared = useCallback(() => {
    // "Limpiar planificación" ya se aplicó en la BD (contenido de las clases
    // no aprobadas). Se descarta cualquier edición pendiente sin guardar de
    // esta sección (quedaría desincronizada) y se fuerza recarga del calendario.
    setPendingPlannedClasses({});
    lastReplicatedKeysRef.current = [];
    setCalendarKey(prev => prev + 1);
    oaClassMatrix.loadMatrix();
  }, [oaClassMatrix]);

  // El BulkAIPromptModal ya entrega el objeto pending listo para merge
  const handleBulkAIGenerated = useCallback((pendingClasses) => {
    setPendingPlannedClasses(prev => ({ ...prev, ...pendingClasses }));
  }, []);

  const handleSaveUnitOAs = (unitId, oas) => {
    // This function needs to be implemented
    console.log(unitId, oas);
    handleCloseUnitOAModal();
  };

  // Combinar OAs de unidades regulares con OAs de Unidad 0 para el calendario
  const oasForUnits = units.flatMap(unit => {
    if (unit.short_name === 'UN0') {
      // Para Unidad 0, combinar OAs locales con OAs del backend
      const backendOAs = oas.filter(oa => {
        const oaUnitId = parseInt(oa.unit_id);
        const unitOrgId = parseInt(unit.organization_id);
        return oaUnitId === unitOrgId;
      });
      
      // Mapear OAs locales para usar IDs reales si están disponibles
      const mappedUnit0OAs = unit0OAs.map(oa => {
        // Si hay un mapeo para este ID temporal, usar el ID real
        if (unit0OAMapping[oa.competency_definition_id]) {
          return {
            ...oa,
            competency_definition_id: unit0OAMapping[oa.competency_definition_id]
          };
        }
        // Si no hay mapeo, mantener el ID temporal
        return { ...oa };
      });
      
      return [...mappedUnit0OAs, ...backendOAs];
    } else {
      // Para unidades regulares, usar OAs de la base de datos
      return oas.filter(oa => parseInt(oa.unit_id) === parseInt(unit.organization_id));
    }
  });

  if (loading) {
    return (
      <SidebarProvider>
        <div className="global-layout">
          <SidebarContainer />
          <ContentContainer>
          <div className="global-content-header">
            <Breadcrumb />
            <div className="d-flex align-items-center gap-3 mt-3">
              <div>
                <h1 className="global-title">Vista Docente</h1>
                <p className="global-subtitle text-muted">
                  Gestiona tu proceso de enseñanza
                </p>
              </div>
            </div>
          </div>
          <div className="global-content-body">
            <div className="text-center">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Cargando...</span>
              </Spinner>
              <p className="mt-2">Cargando datos del currículum...</p>
            </div>
          </div>
        </ContentContainer>
      </div>
      </SidebarProvider>
    );
  }

  return (
    <>
    <SidebarProvider>
      <div className="global-layout">
        <SidebarContainer />

        <ContentContainer>

        <div className="global-content-body">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Navigate to="/docente/inicio" replace />} />
              <Route path="/inicio" element={<TeacherDashboard />} />
              <Route path="/clases" element={
                <div>
                  <div className="d-flex justify-content-end mb-2">
                    <Button
                      variant="light"
                      size="sm"
                      className="btn-artemis btn-artemis-sm btn-artemis-ghost btn-artemis-icon"
                      onClick={() => setRunTour(true)}
                      title="Ver el tour guiado de este módulo"
                    >
                      <QuestionCircleFill /> Ayuda
                    </Button>
                  </div>
                  <Tabs defaultActiveKey="planning" id="teacher-view-tabs" className="mb-3">
                    <Tab eventKey="planning" title="Planificación Curricular">
                      <CurriculumSelector
                        onSelectionComplete={handleSelectionCompleteLocal}
                        selectedSubject={selectedSubject}
                        selectedCourse={selectedCourse}
                        selectedCourseSection={selectedCourseSection}
                        onBack={handleBackToSelection}
                        onSave={handleSavePlanning}
                        hasChanges={
                          Object.keys(pendingPlannedClasses).length > 0 ||
                          shouldDeleteUnit0 ||
                          unit0OAs.length > 0
                        }
                      />
                      {selectionComplete && selectedCourse && (
                        <>
                          {loadingContent ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                              <div className="text-center">
                                <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }}>
                                  <span className="visually-hidden">Cargando...</span>
                                </Spinner>
                                <p className="mt-3 text-muted">Cargando contenido del curso...</p>
                              </div>
                            </div>
                          ) : (
                            <>
                              {/* Overlay de carga mientras el calendario carga */}
                              {loadingCalendar && (
                                <div style={{
                                  position: 'fixed',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                  zIndex: 9999,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <div className="text-center">
                                    <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }}>
                                      <span className="visually-hidden">Cargando...</span>
                                    </Spinner>
                                    <p className="mt-3 text-muted">Cargando calendario de planificación...</p>
                                  </div>
                                </div>
                              )}
                              
                              <CurriculumAccordion
                                units={units}
                                oas={oas}
                                subjectId={selectedSubject}
                                hasUnit0={hasUnit0}
                                unit0OAs={unit0OAs}
                                onManageOAsClick={handleManageOAsClick}
                                onCreateUnit0={createUnit0}
                                onDeleteUnit0={deleteUnit0}
                                selectedCourse={selectedCourse}
                                selectedCourseSection={selectedCourseSection}
                                onReplicatePlanning={handleReplicatePlanning}
                                onBulkAIClick={() => setShowBulkAIModal(true)}
                                onPlanningCleared={handlePlanningCleared}
                                matrix={oaClassMatrix}
                                onDesasociarKeys={handleUnassignMatrixKeys}
                                onUnitDatesPersisted={applyUnitValidDates}
                              />
                              <PlanningCalendar
                                ref={planningCalendarRef}
                                key={calendarKey}
                                organizationId={selectedCourseSection ? parseInt(selectedCourseSection) : null}
                                onCellClick={handleCellClick}
                                pendingPlannedClasses={pendingPlannedClasses}
                                onLoadingChange={handleCalendarLoadingChange}
                                blocksMode
                                reloadToken={calendarReloadToken}
                                matrixPendingCount={oaClassMatrix.pendingCount}
                                matrixSaving={oaClassMatrix.saving}
                                matrixError={oaClassMatrix.error}
                                onUpdateMatrix={handleUpdateMatrix}
                                onDismissMatrixError={() => oaClassMatrix.setError('')}
                              />
                            </>
                          )}
                        </>
                      )}
                      {!selectionComplete && (
                        <div className="text-center mt-4">
                          <p className="text-muted">Seleccione una asignatura, curso y sección para ver el contenido curricular.</p>
                        </div>
                      )}
                    </Tab>
                    <Tab eventKey="progress" title="Avance Curricular">
                      {selectionComplete && selectedCourseSection ? (
                        <div className="p-3">
                          <ClassExecutionCalendar 
                            key={`execution-calendar-${calendarKey}`}
                            sectionId={parseInt(selectedCourseSection)}
                            onLoadingChange={handleCalendarLoadingChange}
                          />
                        </div>
                      ) : (
                        <div className="p-3 text-center">
                          <p className="text-muted">Seleccione una asignatura, curso y sección para ver el calendario de seguimiento de clases.</p>
                        </div>
                      )}
                    </Tab>
                  </Tabs>
                </div>
              } />
              <Route path="/planificacion" element={<TeacherPlanningMonitor />} />
              <Route path="/estudiantes" element={<div>Estudiantes</div>} />
              <Route path="/evaluacion" element={<div>Evaluación</div>} />
              <Route path="/soporte" element={<SoporteView />} />
            </Routes>
          </Suspense>

          <ProductTour
            steps={MIS_CLASES_TOUR_STEPS}
            run={runTour}
            onClose={handleCloseTour}
            onFinish={handleCloseTour}
          />

          {planningModalData && (
            <PlanningModal
              show={showPlanningModal}
              handleClose={handleClosePlanningModal}
              handleSave={handleSavePlan}
              data={planningModalData}
              courseSectionId={parseInt(selectedCourseSection)}
              pendingPlannedClasses={pendingPlannedClasses}
              oas={oasForUnits}
              onNavigate={handleNavigatePlanning}
              canNavigatePrev={!!prevPlanningSlot}
              canNavigateNext={!!nextPlanningSlot}
            />
          )}

          {selectedUnit && (
            <UnitOAManager
              show={showUnitOAModal}
              handleClose={handleCloseUnitOAModal}
              unit={selectedUnit}
              onSave={handleSaveUnitOAs}
            />
          )}

          <Unit0OAManager
            show={showUnit0OAModal}
            onHide={handleCloseUnit0OAModal}
            subjectId={selectedSubject}
            unit0OAs={unit0OAs}
            backendOAs={oas.filter(oa => {
              // Obtener OAs del backend que pertenecen a la Unidad 0
              const unit0 = units.find(unit => unit.short_name === 'UN0');
              if (!unit0) return false;
              const oaUnitId = parseInt(oa.unit_id);
              const unitOrgId = parseInt(unit0.organization_id);
              return oaUnitId === unitOrgId;
            })}
            onAddOA={addOAToUnit0}
            onUpdateOA={updateOAInUnit0}
            onDeleteOA={deleteOAFromUnit0}
          />

          <Suspense fallback={<LoadingSpinner />}>
            <ErrorModal
              show={showErrorModal}
              onHide={() => setShowErrorModal(false)}
              error={errorDetails}
              title="Error al Guardar Planificación"
            />
          </Suspense>

          <Modal
            show={showNoOAWarning}
            onHide={() => setShowNoOAWarning(false)}
            centered
            dialogClassName="pm-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title>Objetivos de Aprendizaje requeridos</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Debes asociar Objetivos de aprendizaje a esta clase antes de poder planificarlas.
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                size="sm"
                className="btn-artemis btn-artemis-secondary"
                onClick={() => setShowNoOAWarning(false)}
              >
                Aceptar
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            show={!!clearPlanningConfirm}
            onHide={() => setClearPlanningConfirm(null)}
            centered
            dialogClassName="pm-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title>Esta clase ya tiene una planificación</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                {clearPlanningConfirm && clearPlanningConfirm.targets.length > 1
                  ? 'Estás cambiando los Objetivos de Aprendizaje de clases que ya tienen una planificación.'
                  : 'Estás cambiando los Objetivos de Aprendizaje de una clase que ya tiene una planificación.'}{' '}
                Si continúas, se <strong>eliminará la planificación</strong>
                {clearPlanningConfirm && clearPlanningConfirm.targets.length > 1
                  ? ' de esas clases'
                  : ' de esa clase'}{' '}
                (objetivo, etapas, comentarios y archivos adjuntos), y{' '}
                {clearPlanningConfirm && clearPlanningConfirm.targets.length > 1
                  ? 'volverán'
                  : 'volverá'}{' '}
                a quedar sin planificar.
              </p>
              <p className="mb-2 text-muted small">
                <strong>Restaurar</strong> deshace los cambios de OA de{' '}
                {clearPlanningConfirm && clearPlanningConfirm.targets.length > 1
                  ? 'estas clases y conserva sus planificaciones'
                  : 'esta clase y conserva su planificación'} intacta.
              </p>
              {clearPlanningConfirm && (
                <ul className="mb-0">
                  {clearPlanningConfirm.targets.map(t => {
                    const [y, m, d] = t.class_date.split('-');
                    return (
                      <li key={t.slot} className="mb-2">
                        <strong>{`${d}-${m}-${y}`}</strong>
                        {t.block_number > 1 ? ` · Bloque ${t.block_number}` : ''}
                        <div className="text-muted">
                          {t.objective && t.objective.trim()
                            ? t.objective
                            : '(sin objetivo escrito)'}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setClearPlanningConfirm(null)}
                disabled={oaClassMatrix.saving}
              >
                Cancelar
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleRestoreClearPlanning}
                disabled={oaClassMatrix.saving}
              >
                Restaurar
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleConfirmClearPlanning}
                disabled={oaClassMatrix.saving}
              >
                Continuar
              </Button>
            </Modal.Footer>
          </Modal>

          <Suspense fallback={null}>
            <BulkAIPromptModal
              show={showBulkAIModal}
              onHide={() => setShowBulkAIModal(false)}
              onBulkGenerated={handleBulkAIGenerated}
              oas={oasForUnits}
              selectedCourseSection={selectedCourseSection}
              pendingPlannedClasses={pendingPlannedClasses}
            />
          </Suspense>
        </div>
      </ContentContainer>
    </div>
    </SidebarProvider>
    </>
  );
};

export default TeacherView;
